/**
 * Simple OCR Test Page
 *
 * Quick way to test OCR without auth complexity.
 * Upload an image, see the extracted text, copy it for review.
 */

import { useState, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@/components/ui/Button';
import {
  Upload,
  FileImage,
  Copy,
  Check,
  Loader2,
  ScanLine,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { RoleGuard } from '@/components/layout/RoleGuard';

export const Route = createFileRoute('/_app/ocr/test')({
  component: () => (
    <RoleGuard allowedRoles={['owner', 'supervisor', 'operator']}>
      <OcrTestPage />
    </RoleGuard>
  ),
});

const OCR_SERVICE_URL = 'http://localhost:8001';

interface OcrResult {
  success: boolean;
  filename?: string;
  raw_ocr_text?: string;
  text_length?: number;
  error?: string;
}

interface FullPipelineResult {
  success: boolean;
  filename?: string;
  raw_ocr_text?: string;
  extracted_data?: {
    date?: string;
    farm_name?: string;
    records?: Array<Record<string, unknown>>;
  };
  model?: string;
  error?: string;
  step?: string;
}

function OcrTestPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<OcrResult | null>(null);
  const [pipelineResult, setPipelineResult] = useState<FullPipelineResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPipelineLoading, setIsPipelineLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setResult(null);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(selected);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped && dropped.type.startsWith('image/')) {
      setFile(dropped);
      setResult(null);

      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(dropped);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${OCR_SERVICE_URL}/debug/test-ocr`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      setResult(data);
    } catch (error) {
      setResult({
        success: false,
        error: `Failed to connect to OCR service: ${error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFullPipeline = async () => {
    if (!file) return;

    setIsPipelineLoading(true);
    setPipelineResult(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${OCR_SERVICE_URL}/debug/test-full-pipeline`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      setPipelineResult(data);
    } catch (error) {
      setPipelineResult({
        success: false,
        error: `Failed to connect: ${error}`,
      });
    } finally {
      setIsPipelineLoading(false);
    }
  };

  const handleCopyWithPrompt = async () => {
    if (result?.raw_ocr_text) {
      const prompt = `You are parsing OCR text from a Philippine shrimp farm logbook. The OCR loses table structure, so you must carefully reconstruct it.

The logbook uses Excel-format slots (5 times per day):
- 7am: Morning readings (typically: salinity, alkalinity AM, temp, depth, transparency, color, pH, weather)
- 10am: Mid-morning readings (typically: DO, pH, temp)
- 1pm: Afternoon readings (typically: all params + NH3, NO2, Cal/Mag, alkalinity PM)
- 4pm: Late afternoon readings (typically: DO, pH, temp)
- 7pm: Evening readings (typically: DO, pH, temp)

LOGBOOK COLUMN ORDER (left to right):
1. POND - ID like ASA1, ASB2, ASPE (4 chars)
2. DOC - Days of culture (2-3 digit number, typically 14-120)
3. SALINITY - ppt (typically 10-35, may show multiple slots per day)
4. ALKALINITY - ppm (often shown as AM/PM like "130/130")
5. TEMP - °C (typically 26-32)
6. DEPTH - meters (typically 0.8-1.5)
7. TRANS - transparency cm (typically 25-60)
8. COLOR - code like YG, G, LG, DG
9. pH - (typically 7.0-8.5, may appear multiple times)
10. DO - dissolved oxygen mg/L (typically 4-10, may appear multiple times)
11. NH3 - ammonia ppm (typically 0-2)
12. NO2 - nitrite ppm (typically 0-0.5)
13. CAL/MAG - ratio like "260/814"

PARSING TIPS:
- Each row starts with a pond ID (ASA1, ASA2, ASB1, etc.)
- Numbers like "7.67.7" mean "7.6" and "7.7" (two readings)
- Look for DATE in format MM-DD-YY
- Alkalinity values 100-150 are normal (don't confuse with temp)

Extract to this JSON format (create ONE record per slot):
{
  "records": [
    {
      "pond_id": "ASA1",
      "doc": 98,
      "reading_date": "YYYY-MM-DD",
      "reading_slot": "7am",
      "ph": 7.6,
      "temperature_c": 28,
      "dissolved_oxygen": 5.2,
      "salinity_ppt": 14,
      "alkalinity_am": 130,
      "transparency_cm": 45,
      "water_color": "YG",
      "depth_m": 1.2,
      "weather": null
    },
    {
      "pond_id": "ASA1",
      "doc": 98,
      "reading_date": "YYYY-MM-DD",
      "reading_slot": "3pm",
      "ph": 7.7,
      "temperature_c": 29,
      "dissolved_oxygen": 7.0,
      "ammonia_ppm": 0.2,
      "nitrite_ppm": 0.10,
      "alkalinity_pm": 130,
      "calmag": "260/814"
    }
  ]
}

Use null for values you cannot reliably extract. Save result to extracted_data.json

OCR TEXT:
---
${result.raw_ocr_text}
---`;

      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };


  return (
    <div className="min-h-full bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <ScanLine className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">OCR Test</h1>
              <p className="text-sm text-gray-500">
                Quick test - upload an image and get the extracted text
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto">
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Upload Area */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">1. Upload Image</h2>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
                'hover:border-primary-400 hover:bg-primary-50/50',
                preview ? 'border-primary-300 bg-primary-50/30' : 'border-gray-300'
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              {preview ? (
                <div className="space-y-3">
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded-lg shadow-sm"
                  />
                  <p className="text-sm text-gray-600">{file?.name}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <FileImage className="w-12 h-12 text-gray-400 mx-auto" />
                  <div>
                    <p className="text-gray-600">Drop image here or click to select</p>
                    <p className="text-sm text-gray-400 mt-1">JPG, PNG, HEIC supported</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleFullPipeline}
                disabled={!file || isPipelineLoading}
                leftIcon={isPipelineLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
              >
                {isPipelineLoading ? 'Processing...' : 'Full pipeline'}
              </Button>
              <Button
                variant="ghost"
                onClick={handleUpload}
                disabled={!file || isLoading}
                leftIcon={isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              >
                {isLoading ? '...' : 'OCR Only'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Full pipeline = text recognition + parsing
            </p>
          </div>

          {/* Result Area */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">2. Results</h2>

            {/* Full pipeline result */}
            {pipelineResult ? (
              pipelineResult.success ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Check className="w-4 h-4" />
                    <span>Extracted {pipelineResult.extracted_data?.records?.length || 0} records via {pipelineResult.model}</span>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Date: {pipelineResult.extracted_data?.date || 'Unknown'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(pipelineResult.extracted_data, null, 2));
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        leftIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      >
                        {copied ? 'Copied!' : 'Copy JSON'}
                      </Button>
                    </div>
                    <pre className="text-xs overflow-auto max-h-64 whitespace-pre-wrap font-mono">
                      {JSON.stringify(pipelineResult.extracted_data, null, 2)}
                    </pre>
                  </div>

                  <p className="text-xs text-gray-500">
                    Review the JSON above before using it.
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Pipeline Failed at: {pipelineResult.step || 'unknown'}</p>
                    <p className="text-sm mt-1">{pipelineResult.error}</p>
                    {pipelineResult.raw_ocr_text && (
                      <details className="mt-2">
                        <summary className="text-sm cursor-pointer">Show OCR text</summary>
                        <pre className="text-xs mt-2 whitespace-pre-wrap">{pipelineResult.raw_ocr_text}</pre>
                      </details>
                    )}
                  </div>
                </div>
              )
            ) : result ? (
              /* OCR Only Result */
              result.success ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-500">
                    {result.text_length} characters extracted (OCR only)
                  </div>
                  <pre className="bg-gray-50 rounded-lg p-4 text-sm overflow-auto max-h-64 whitespace-pre-wrap font-mono border">
                    {result.raw_ocr_text}
                  </pre>

                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Manual parsing (if the parser is unavailable)
                    </h3>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCopyWithPrompt}
                      leftIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    >
                      {copied ? 'Copied!' : 'Copy for review'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">OCR Failed</p>
                    <p className="text-sm mt-1">{result.error}</p>
                  </div>
                </div>
              )
            ) : (
              <div className="text-center py-12 text-gray-400">
                <ScanLine className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Upload an image and click "Full pipeline"</p>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-medium text-amber-800 mb-2">How to use:</h3>
          <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
            <li>Upload a logbook photo</li>
            <li>Click "Extract Text" to run OCR</li>
            <li>Click "Copy with Prompt" to copy text + parsing instructions</li>
            <li>Review the extracted JSON</li>
            <li>Use the JSON to import data into HiponHero</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
